import { supabase } from "./supabase";
import type { User, Session, Provider } from "@supabase/supabase-js";

// Master accounts with full access to all portals
// Configure via VITE_MASTER_ACCOUNTS environment variable (comma-separated emails)
// Example: VITE_MASTER_ACCOUNTS=admin@example.com,owner@example.com
const masterAccountsEnv = import.meta.env.VITE_MASTER_ACCOUNTS || '';
export const MASTER_ACCOUNTS: string[] = masterAccountsEnv
  .split(',')
  .map((email: string) => email.trim().toLowerCase())
  .filter((email: string) => email.length > 0);

// Tenant user roles from database
export type TenantUserRole = "owner" | "manager" | "dispatcher" | "technician";

// Portal access permissions by role
export type UserRole = "master" | "sales" | "business" | "technician" | "analytics";

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  role: UserRole;
  allowed_portals: string[];
  created_at: string;
}

export interface TenantUserData {
  id: string;
  tenant_id: string;
  auth_user_id: string;
  email: string;
  name: string;
  role: TenantUserRole;
  permissions: Record<string, boolean>;
  is_active: boolean;
  tenant?: {
    id: string;
    name: string;
    slug: string;
  };
}

// Map tenant user roles to portal access
// Only technicians are restricted - everyone else gets full access
export function mapRoleToPortals(role: TenantUserRole): string[] {
  if (role === "technician") {
    return ["technician"];
  }
  // Owner, manager, dispatcher all get full access
  return ["business", "technician"];
}

// Check if user is a master account
export function isMasterAccount(email: string | undefined): boolean {
  if (!email) return false;
  return MASTER_ACCOUNTS.includes(email.toLowerCase());
}

// Get allowed portals for a user (static check for master accounts)
export function getAllowedPortals(email: string | undefined): string[] {
  if (!email) return [];

  if (isMasterAccount(email)) {
    return ["sales", "business", "technician", "analytics"];
  }

  // Default: return empty array, will be populated from database via fetchUserTenantRoles
  return [];
}

// Fetch user's tenant roles from database
export async function fetchUserTenantRoles(userId: string): Promise<TenantUserData[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("tenant_users")
    .select(`
      id,
      tenant_id,
      auth_user_id,
      email,
      name,
      role,
      permissions,
      is_active,
      tenant:tenants (
        id,
        name,
        slug
      )
    `)
    .eq("auth_user_id", userId)
    .eq("is_active", true);

  if (error) {
    if (import.meta.env.DEV) console.error("Error fetching tenant roles:", error);
    return [];
  }

  return (data || []) as TenantUserData[];
}

// Get all allowed portals for a user based on their tenant roles
export function getPortalsFromTenantRoles(tenantRoles: TenantUserData[]): string[] {
  const portals = new Set<string>();

  for (const tenantUser of tenantRoles) {
    const rolePortals = mapRoleToPortals(tenantUser.role);
    rolePortals.forEach(p => portals.add(p));
  }

  return Array.from(portals);
}

// Fetch all tenants for master accounts (they have access to all)
export async function fetchAllTenantsForMaster(): Promise<TenantUserData[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("tenants")
    .select("id, name, slug")
    .eq("onboarding_status", "active")
    .order("name", { ascending: true });

  if (error) {
    if (import.meta.env.DEV) console.error("Error fetching tenants for master:", error);
    return [];
  }

  // Convert tenants to TenantUserData format (master acts as owner of all)
  return (data || []).map((tenant) => ({
    id: `master-${tenant.id}`,
    tenant_id: tenant.id,
    auth_user_id: "master",
    email: "",
    name: "Master Admin",
    role: "owner" as TenantUserRole,
    permissions: {},
    is_active: true,
    tenant: {
      id: tenant.id,
      name: tenant.name,
      slug: tenant.slug,
    },
  }));
}

// Sign in with OAuth provider
export async function signInWithProvider(provider: Provider) {
  if (!supabase) {
    throw new Error("Supabase client not initialized");
  }

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) throw error;
  return data;
}

// Sign in with Google
export async function signInWithGoogle() {
  return signInWithProvider("google");
}

// Sign in with email (magic link)
export async function signInWithEmail(email: string) {
  if (!supabase) {
    throw new Error("Supabase client not initialized");
  }

  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) throw error;
  return data;
}

// Sign in with email and password
export async function signInWithPassword(email: string, password: string) {
  if (!supabase) {
    throw new Error("Supabase client not initialized");
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  return data;
}

// Sign out
export async function signOut() {
  if (!supabase) {
    throw new Error("Supabase client not initialized");
  }

  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// Get current session
export async function getSession(): Promise<Session | null> {
  if (!supabase) return null;

  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

// Get current user
export async function getUser(): Promise<User | null> {
  if (!supabase) return null;

  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

// Subscribe to auth state changes
export function onAuthStateChange(callback: (event: string, session: Session | null) => void) {
  if (!supabase) return { data: { subscription: { unsubscribe: () => {} } } };

  return supabase.auth.onAuthStateChange(callback);
}
